import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Wallet, Globe, ArrowDownLeft, ArrowUpRight, Loader2, Plus } from "lucide-react";
import { walletActivity, formatUSD } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import WalletModal from "@/components/WalletModal";

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function Account() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletOpen, setWalletOpen] = useState(false);

  const load = () => walletActivity().then(setData).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/");
      return;
    }
    load();
  }, [authLoading, user, navigate]);

  if (authLoading || (loading && user)) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="account-loading">
        <Loader2 size={28} className="animate-spin text-gold" />
      </div>
    );
  }
  if (!user) return null;

  const stats = [
    { label: "Wallet Balance", value: formatUSD(data?.wallet_balance), icon: <Wallet size={16} /> },
    { label: "Domains Owned", value: data?.purchases?.length ?? 0, icon: <Globe size={16} /> },
    { label: "Total Invested", value: formatUSD(data?.total_spent), icon: <ArrowUpRight size={16} /> },
    { label: "Total Topped Up", value: formatUSD(data?.total_topped_up), icon: <ArrowDownLeft size={16} /> },
  ];

  return (
    <div className="min-h-screen bg-bg" data-testid="account-page">
      <header className="border-b border-line bg-[var(--nav-bg)] backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="account-logo" className="flex items-center gap-3">
            <span className="w-2 h-2 rotate-45 bg-gold" />
            <span className="font-mono text-lg text-ink">elite-domains<span className="text-gold">.com</span></span>
          </Link>
          <Link to="/" data-testid="account-back" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-dim hover:text-ink transition-colors">
            <ArrowLeft size={14} /> Collection
          </Link>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="overline mb-4">My Account</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-white-fade mb-2">
            Welcome, <span className="italic text-cosmic">{user.name || user.email.split("@")[0]}</span>
          </h1>
          <p className="text-ink-dim text-sm mb-12">{user.email}</p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {stats.map((s) => (
            <div key={s.label} className="bg-[var(--panel-soft)] border border-line rounded-2xl p-6" data-testid={`account-stat-${s.label}`}>
              <div className="flex items-center gap-2 text-gold mb-3">{s.icon}</div>
              <div className="font-mono text-2xl md:text-3xl text-ink mb-1">{s.value}</div>
              <div className="overline">{s.label}</div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setWalletOpen(true)}
          data-testid="account-topup-btn"
          className="mb-16 inline-flex items-center gap-2 rounded-full bg-gold text-bg font-sans font-bold text-sm px-7 py-3.5 hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow"
        >
          <Plus size={16} /> Top up wallet
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Domains owned */}
          <section data-testid="account-domains">
            <div className="flex items-center gap-3 mb-6">
              <Globe size={16} className="text-gold" />
              <h2 className="font-serif text-3xl text-ink">Domains you own</h2>
            </div>
            {data?.purchases?.length ? (
              <div className="space-y-3">
                {data.purchases.map((p) => (
                  <div key={p.id} className="bg-[var(--panel-soft)] border border-line rounded-xl p-5 flex items-center justify-between" data-testid={`owned-${p.domain_name}`}>
                    <div>
                      <div className="font-mono text-lg text-gold-gradient">{p.domain_name}</div>
                      <div className="font-mono text-[11px] uppercase tracking-wider text-ink-dim mt-1">
                        {fmtDate(p.created_at)} · via {p.method === "wallet" ? "Wallet" : "Card"}
                      </div>
                    </div>
                    <div className="font-mono text-ink">{formatUSD(p.amount)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[var(--panel-soft)] border border-line rounded-xl p-8 text-center" data-testid="account-domains-empty">
                <p className="text-ink-dim text-sm mb-4">You don't own any domains yet.</p>
                <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-gold hover-underline">Browse the collection</Link>
              </div>
            )}
          </section>

          {/* Wallet history */}
          <section data-testid="account-history">
            <div className="flex items-center gap-3 mb-6">
              <Wallet size={16} className="text-gold" />
              <h2 className="font-serif text-3xl text-ink">Wallet history</h2>
            </div>
            {data && (data.topups.length || data.purchases.length) ? (
              <div className="space-y-3">
                {[
                  ...data.topups.map((t) => ({ kind: "topup", amount: t.amount, date: t.created_at, key: t.session_id })),
                  ...data.purchases.map((p) => ({ kind: "spend", amount: p.amount, date: p.created_at, key: p.id, name: p.domain_name })),
                ]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((row) => (
                    <div key={row.key} className="bg-[var(--panel-soft)] border border-line rounded-xl p-5 flex items-center justify-between" data-testid={`history-${row.kind}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-9 h-9 rounded-full flex items-center justify-center ${row.kind === "topup" ? "bg-emerald-500/10 text-emerald-400" : "bg-gold/10 text-gold"}`}>
                          {row.kind === "topup" ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                        </span>
                        <div>
                          <div className="text-ink text-sm">{row.kind === "topup" ? "Wallet top-up" : `Acquired ${row.name}`}</div>
                          <div className="font-mono text-[11px] uppercase tracking-wider text-ink-dim">{fmtDate(row.date)}</div>
                        </div>
                      </div>
                      <div className={`font-mono ${row.kind === "topup" ? "text-emerald-400" : "text-ink-dim"}`}>
                        {row.kind === "topup" ? "+" : "−"}{formatUSD(row.amount)}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="bg-[var(--panel-soft)] border border-line rounded-xl p-8 text-center" data-testid="account-history-empty">
                <p className="text-ink-dim text-sm">No wallet activity yet. Top up to get started.</p>
              </div>
            )}
          </section>
        </div>
      </div>

      <WalletModal open={walletOpen} onOpenChange={(v) => { setWalletOpen(v); if (!v) load(); }} />
    </div>
  );
}
